import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { selectTenant } from "../lib/tenantSession.js";
import { connectRedirectOf } from "../lib/userConnections.js";
import { PasswordInput } from "../components/PasswordInput.js";

// A redirect-after-login target is only valid for a specific surface:
//   - "hub":    /admin/*  (AdminHome shell)
//   - "tenant": /app/*    (TenantApp shell — requires active_tenant_id)
// Honoring a /app/* redirect when sending the user to hub (or before a tenant
// has been selected) bounces them into TenantApp without an active tenant,
// which 401s and loops back to /login — keep filtered out.
function safeRedirect(redirectTo: string | null, surface: "hub" | "tenant", fallback: string): string {
  if (!redirectTo) return fallback;
  const prefix = surface === "hub" ? "/admin" : "/app";
  if (redirectTo === prefix || redirectTo.startsWith(`${prefix}/`) || redirectTo.startsWith(`${prefix}?`)) {
    return redirectTo;
  }
  return fallback;
}

type Contexts = {
  hub: { user: { id: string; email: string; role: string } };
  tenant_memberships: Array<{
    tenant_id: string;
    tenant_name: string;
    tenant_slug: string;
    auth_mode: string;
    tenant_user_id: string;
    email: string;
    role: string;
    status: string;
  }>;
};

export function Login() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");
  const [checkingSession, setCheckingSession] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contexts, setContexts] = useState<Contexts | null>(null);
  // Selected destination: "hub" or a tenant_id
  const [selectedDestination, setSelectedDestination] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    const connectRedirect = connectRedirectOf(redirectTo);

    void (async () => {
      try {
        // Session resume path:
        // 1) if active tenant is already selected, go straight to /app
        // 2) otherwise choose hub/tenant routing based on available contexts
        try {
          await apiFetch("/api/tenant/me");
          if (cancelled) return;
          // §5.1: an authenticated user returning to a /connect flow goes there
          // directly (no tenant detour).
          if (connectRedirect) { nav(connectRedirect, { replace: true }); return; }
          nav(safeRedirect(redirectTo, "tenant", "/app"), { replace: true });
          return;
        } catch {
          // Not in a tenant context (or not logged in) — continue probing.
        }

        const ctx = await apiFetch<Contexts>("/api/auth/contexts");
        if (cancelled) return;

        // Session exists (contexts resolved) → honor a /connect redirect now,
        // before any hub/tenant destination routing.
        if (connectRedirect) { nav(connectRedirect, { replace: true }); return; }

        const isHubUser = ctx.hub.user.role !== null;
        const activeTenants = ctx.tenant_memberships.filter((m) => m.status === "active");

        // Remembered context is hub (active_tenant_id is not set).
        if (isHubUser) {
          nav(safeRedirect(redirectTo, "hub", "/admin"), { replace: true });
          return;
        }

        // Tenant-only user with a single active membership can resume directly.
        if (activeTenants.length === 1) {
          await selectTenant(activeTenants[0].tenant_id, activeTenants[0].tenant_name);
          if (cancelled) return;
          nav(safeRedirect(redirectTo, "tenant", "/app"), { replace: true });
          return;
        }

        // Tenant-only user with multiple memberships needs a destination pick.
        if (activeTenants.length > 1) {
          setContexts({ ...ctx, tenant_memberships: activeTenants });
          setSelectedDestination(activeTenants[0]?.tenant_id ?? "");
          return;
        }

        // Session exists but no active access remains.
        await apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
      } catch {
        // No active session; show login form.
      } finally {
        if (!cancelled) setCheckingSession(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [nav, redirectTo]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await apiFetch<{ ok: true }>("/api/auth/login", {
        method: "POST",
        body: { email, password }
      });

      const ctx = await apiFetch<Contexts>("/api/auth/contexts");
      const isHubUser = ctx.hub.user.role !== null;
      const activeTenants = ctx.tenant_memberships.filter((m) => m.status === "active");

      // No accessible destinations — account is disabled or has no active memberships
      if (!isHubUser && activeTenants.length === 0) {
        await apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
        setError("Your account has been disabled. Contact your administrator to restore access.");
        return;
      }

      // §5.1: a /connect flow is honored immediately after successful auth,
      // before hub/tenant destination routing (consent needs no active tenant).
      const connectRedirect = connectRedirectOf(redirectTo);
      if (connectRedirect) {
        nav(connectRedirect);
        return;
      }

      // Auto-route: hub-only user
      if (isHubUser && activeTenants.length === 0) {
        nav(safeRedirect(redirectTo, "hub", "/admin"));
        return;
      }

      // Auto-route: single tenant, no hub access
      if (!isHubUser && activeTenants.length === 1) {
        await selectTenant(activeTenants[0].tenant_id, activeTenants[0].tenant_name);
        nav(safeRedirect(redirectTo, "tenant", "/app"));
        return;
      }

      // Multiple options — show single dropdown picker
      const stored = { ...ctx, tenant_memberships: activeTenants };
      setContexts(stored);
      // Default to hub if available, otherwise first tenant
      const defaultDest = isHubUser ? "hub" : (activeTenants[0]?.tenant_id ?? "");
      setSelectedDestination(defaultDest);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  if (checkingSession) {
    return (
      <div className="centerPage">
        <div className="card pageNarrow wFull">
          <div className="muted">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="centerPage">
      <div className="card pageNarrow wFull">
        <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Sign in</div>
        <div className="help" style={{ marginBottom: 12 }}>
          Sign in to your account.
        </div>

        {!contexts ? (
          <form onSubmit={(e) => void onSubmit(e)} style={{ display: "grid", gap: 10 }}>
          <label className="field">
            <span className="label">Email</span>
            <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          </label>

          <label className="field">
            <span className="label">Password</span>
            <PasswordInput value={password} onChange={setPassword} autoComplete="current-password" aria-label="Password" />
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -2 }}>
            <Link to="/forgot-password" className="muted" style={{ fontSize: 13 }}>
              Forgot password?
            </Link>
          </div>

          <button type="submit" disabled={loading || email.trim() === "" || password === ""} className="btn btnPrimary">
            Sign in
          </button>
          </form>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>
              Signed in as <b style={{ color: "var(--text)" }}>{contexts.hub.user.email}</b>
            </div>
            <label className="field">
              <span className="label">Go to</span>
              <select
                className="select"
                value={selectedDestination}
                onChange={(e) => setSelectedDestination(e.target.value)}
                autoFocus
              >
                {contexts.hub.user.role !== null && (
                  <option value="hub">Hub Console</option>
                )}
                {contexts.tenant_memberships.map((m) => (
                  <option key={m.tenant_id} value={m.tenant_id}>
                    {m.tenant_name}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn btnPrimary"
              type="button"
              disabled={loading || !selectedDestination}
              onClick={async () => {
                if (selectedDestination === "hub") {
                  nav(safeRedirect(redirectTo, "hub", "/admin"));
                  return;
                }
                setLoading(true);
                setError(null);
                try {
                  const picked = contexts.tenant_memberships.find((m) => m.tenant_id === selectedDestination);
                  await selectTenant(selectedDestination, picked?.tenant_name ?? "");
                  nav(safeRedirect(redirectTo, "tenant", "/app"));
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                } finally {
                  setLoading(false);
                }
              }}
            >
              Continue
            </button>
            <button
              className="btn"
              type="button"
              disabled={loading}
              onClick={() => {
                setContexts(null);
                setSelectedDestination("");
              }}
            >
              Back
            </button>
          </div>
        )}

        {error && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 800, color: "var(--danger-text)", marginBottom: 4 }}>Error</div>
            <div className="muted">{error}</div>
          </div>
        )}
      </div>
    </div>
  );
}

